import { memo } from 'react';
import { css } from '@emotion/react';
import { theme } from '@frontend/theme';
import { Button, Chip } from '@frontend/design-system';
import { CALENDAR_Z_INDICES } from '../constants';
import { useCalendarGridStore } from '../store/calendar-grid.store';
import { CalendarNavigationConfig } from '../types';

const TopCornerCellsComponent = ({ onNext, onPrevious, canGoNext, canGoPrevious }: CalendarNavigationConfig) => {
  return (
    <>
      <div data-top-left-corner css={topLeftCornerStyle}>
        <Button
          variant='secondary'
          iconName='caret-left'
          disabled={!canGoPrevious}
          onClick={onPrevious}
          css={{ boxShadow: theme.shadows.light, backgroundColor: theme.colors.neutral5 }}
        />
      </div>
      <div data-top-right-corner css={topRightCornerStyle}>
        <Button variant='secondary' iconName='caret-right' disabled={!canGoNext} onClick={onNext} />
      </div>
    </>
  );
};

const TopCornerCellsForScroll = ({ onScroll }: { onScroll: (direction: 'left' | 'right') => void }) => {
  const { canScrollLeft, canScrollRight } = useCalendarGridStore(['canScrollLeft', 'canScrollRight']);

  return (
    <TopCornerCellsComponent
      onNext={() => onScroll('right')}
      onPrevious={() => onScroll('left')}
      canGoNext={canScrollRight}
      canGoPrevious={canScrollLeft}
    />
  );
};

interface TopCornerCellsProps {
  onScroll: (direction: 'left' | 'right') => void;
  navigationConfig?: CalendarNavigationConfig;
}

export const TopCornerCells = memo(({ onScroll, navigationConfig }: TopCornerCellsProps) => {
  if (navigationConfig) return <TopCornerCellsComponent {...navigationConfig} />;

  return <TopCornerCellsForScroll onScroll={onScroll} />;
});
TopCornerCells.displayName = 'TopCornerCells';

export const BaseTimeZoneChipCell = memo(() => {
  const baseTimezone = useCalendarGridStore((state) => state.groupsById[state.groupIds[0]]?.timezone);

  return <TimezoneChipCell timezone={baseTimezone} />;
});

BaseTimeZoneChipCell.displayName = 'BaseTimeZoneChipCell';

export const TimezoneChipCell = memo(({ timezone }: { timezone: string | undefined }) => {
  if (!timezone) return null;

  return (
    <div data-timezone-chip-cell css={timezoneChipCellStyle}>
      <Chip.TimeZone>{timezone}</Chip.TimeZone>
    </div>
  );
});
TimezoneChipCell.displayName = 'TimezoneChipCell';

const topRightCornerStyle = css({
  gridColumn: '-2 / -1',
  gridRow: 1,
  backgroundColor: theme.colors.neutral5,
  boxShadow: theme.shadows.light,
  zIndex: CALENDAR_Z_INDICES.topCornerCell,
  position: 'sticky',
  top: 0,
  right: 0,
  button: {
    maxWidth: 'var(--group-separator-width)',
    borderRadius: 0,
    height: '100%',
    width: '100%',
  },
});

const topLeftCornerStyle = css({
  zIndex: CALENDAR_Z_INDICES.topCornerCell,
  position: 'sticky',
  top: 0,
  left: 0,
  gridColumn: '1 / 1',
  gridRow: '1 / 2',

  boxShadow: 'none',
  backgroundColor: 'unset',
  button: {
    maxWidth: 'var(--group-separator-width)',
    borderRadius: 0,
    height: '100%',
    width: '100%',
  },
});

// row 3 is the spacer row, directly below the all-day toggle icon in row 2
const timezoneChipCellStyle = css({
  gridColumn: 1,
  gridRow: 3,
  position: 'sticky',
  left: 0,
  zIndex: CALENDAR_Z_INDICES.topCornerCell,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  userSelect: 'none',
  backdropFilter: 'blur(4px)',
  borderBottom: `1px solid ${theme.colors.neutral5}`,
  borderTop: `1px solid ${theme.colors.neutral10}`,
  boxShadow: `0 0 4px ${theme.colors.neutral20}`,
  maxWidth: 'var(--time-column-width)',
});
